export const ALL_GROUPS = { perspective: "group", groupId: null };
export const INDIVIDUAL_VIEW = { perspective: "individual", groupId: null };
const EMPTY_PROGRESS = {
    members: [],
    groups: [],
    target: 1,
    viewerUserId: "",
};
/**
 * The two perspectives, for a segmented switch. Empty when no company walking
 * group has members on this route, so the control hides itself rather than
 * offering a view with nothing in it.
 */
export function routePerspectiveOptions(progress) {
    if (!progress?.groups.length)
        return [];
    return [
        { label: "People", perspective: "individual" },
        { label: "Groups", perspective: "group" },
    ];
}
/**
 * Which group to show in the group perspective. "All groups" comes first, and
 * only when there are two or more groups worth comparing.
 */
export function routeGroupOptions(progress) {
    const groups = progress?.groups ?? [];
    const options = groups.map((group) => ({ label: group.name, groupId: group.id }));
    return groups.length > 1 ? [{ label: "All groups", groupId: null }, ...options] : options;
}
/**
 * The selection to apply when the member switches perspective. Switching to
 * groups lands on the comparison when there is one and on their only group
 * otherwise, so the switch never leaves the map empty.
 */
export function selectionForPerspective(progress, perspective) {
    if (perspective === "individual")
        return INDIVIDUAL_VIEW;
    const groups = progress?.groups ?? [];
    if (groups.length > 1)
        return ALL_GROUPS;
    const only = groups[0];
    return only ? { perspective: "group", groupId: only.id } : INDIVIDUAL_VIEW;
}
/** True when the stored selection still matches something the payload offers. */
export function isSelectableRouteView(progress, selection) {
    if (selection.perspective === "individual")
        return true;
    const groups = progress?.groups ?? [];
    if (!groups.length)
        return false;
    if (!selection.groupId)
        return groups.length > 1;
    return groups.some((group) => group.id === selection.groupId);
}
/**
 * Turn the route progress payload plus the member's selection into everything a
 * map and a roster need. The viewer's own marker is drawn from their auth
 * profile in individual perspective; in group perspective they are already
 * inside their group's marker, so it is suppressed.
 */
export function routeMapView(progress, selection) {
    const payload = progress ?? EMPTY_PROGRESS;
    if (selection.perspective === "individual") {
        // The payload keeps the viewer out of `members` so the map can draw their
        // marker from their own profile. The roster is a standing, though, and a
        // member is never hidden from their own standing, so they are ranked back
        // in here rather than left to each client to remember.
        const ranked = payload.viewer ? [...payload.members, payload.viewer] : [...payload.members];
        return {
            perspective: "individual",
            members: payload.members,
            groups: [],
            rows: ranked.sort(byProgress).map((member) => memberRow(member, payload.viewerUserId)),
            showViewerMarker: true,
            legend: peopleLegend(ranked.length),
        };
    }
    const groups = selection.groupId
        ? payload.groups.filter((group) => group.id === selection.groupId)
        : payload.groups;
    const single = selection.groupId ? groups[0] : undefined;
    const singleRows = single
        ? single.members.length
            ? [...single.members]
                .sort(byProgress)
                .map((member) => memberRow(member, payload.viewerUserId))
            : single.memberCount > 0
                ? [groupRow(single)]
                : []
        : [...groups].sort(byProgress).map(groupRow);
    return {
        perspective: "group",
        members: [],
        groups,
        // One chosen group shows who is inside it. Comparing every group shows the
        // groups themselves. A private roster also uses its aggregate group row so
        // redacted members do not look like an inactive route.
        rows: singleRows,
        showViewerMarker: false,
        legend: single
            ? single.members.length
                ? peopleLegend(single.memberCount)
                : groupLegend(single.memberCount > 0 ? 1 : 0)
            : groupLegend(groups.length),
    };
}
function byProgress(a, b) {
    return b.progressFraction - a.progressFraction;
}
function memberRow(member, viewerUserId) {
    return {
        kind: "member",
        id: member.id,
        title: member.id === viewerUserId ? `${member.displayName} (you)` : member.displayName,
        subtitle: member.groupNames.length ? member.groupNames.join(", ") : null,
        initials: member.initials,
        imageUrl: member.avatarUrl,
        color: member.avatarColor,
        progressFraction: member.progressFraction,
        steps: member.steps,
        distanceMeters: member.distanceMeters,
        isViewer: member.id === viewerUserId,
    };
}
function groupRow(group) {
    return {
        kind: "group",
        id: group.id,
        title: group.name,
        subtitle: `${group.memberCount} ${group.memberCount === 1 ? "member" : "members"}`,
        initials: groupInitials(group.name),
        imageUrl: group.imageUrl,
        color: null,
        progressFraction: group.progressFraction,
        steps: group.steps,
        distanceMeters: group.distanceMeters,
        isViewer: false,
    };
}
export function groupInitials(name) {
    return (name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("") || "?");
}
function peopleLegend(count) {
    if (count <= 0)
        return null;
    return `${count} ${count === 1 ? "person" : "people"}`;
}
function groupLegend(count) {
    if (count <= 0)
        return null;
    return `${count} ${count === 1 ? "group" : "groups"}`;
}
/** How many faces read as a group rather than a list. */
const COMPANION_FACES = 3;
/** Who else is on the route, for a card with one line to spend on it. */
export function journeyCompanions(rows) {
    const people = rows.filter((row) => row.kind === "member");
    const leader = people[0];
    // Companions, so not the member looking at them. The sentence below already
    // counted it that way, which left four faces beside "walking with 3 others".
    const companions = people.filter((row) => !row.isViewer);
    const others = companions.length;
    return {
        faces: companions.slice(0, COMPANION_FACES),
        more: Math.max(0, companions.length - COMPANION_FACES),
        say: !leader || people.length < 2
            ? null
            : `${leader.isViewer ? "You are" : `${leader.title} is`} furthest, ${sharePhrase(leader.progressFraction)} along. ${others === 1 ? "One other is" : `${others} others are`} walking it with you.`,
    };
}
/** A fraction as a person says it, never a decimal read aloud. */
function sharePhrase(fraction) {
    const pct = Math.round(Math.max(0, Math.min(1, fraction)) * 100);
    if (pct < 10)
        return "just started";
    if (pct < 40)
        return "about a quarter";
    if (pct < 60)
        return "about halfway";
    if (pct < 85)
        return "about two thirds";
    return "nearly all the way";
}
//# sourceMappingURL=route-perspective.js.map